import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Eye, Check, Camera, Trash2, LogOut } from "lucide-react";
import { useAuth } from "@/auth";
import { BACKEND_ORIGIN } from "@/api";
import { cn, Button, Field, Spinner } from "@/ui";
import { THEME_LIST, applyTheme } from "@/themes";
import { LEARNING_LEVELS, VOICES, LANGUAGES, TEXT_SIZES, HELP_URL } from "@/constants";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";

export default function Settings() {
  const navigate = useNavigate();
  const { account, updateSettings, uploadAvatar, removeAvatar, signOut } = useAuth();
  const s = account?.settings || {};
  const [name, setName] = useState(account?.name || "");
  const fileRef = useRef(null);
  const [avatarBusy, setAvatarBusy] = useState(false);

  const save = async (patch) => {
    try { await updateSettings(patch); } catch (e) { toast(e.message); }
  };

  const onPickAvatar = async (file) => {
    if (!file) return;
    setAvatarBusy(true);
    try { await uploadAvatar(file); toast("Profile picture updated."); }
    catch (e) { toast(e.message); } finally { setAvatarBusy(false); }
  };
  const onRemoveAvatar = async () => {
    setAvatarBusy(true);
    try { await removeAvatar(); toast("Profile picture removed."); }
    catch (e) { toast(e.message); } finally { setAvatarBusy(false); }
  };

  const initial = (account?.name || "?").trim().charAt(0).toUpperCase();
  const selectCls = "rounded-lg border border-input bg-background px-3 py-1.5 text-sm text-foreground outline-none";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-background/80 px-4 py-3 backdrop-blur-xl">
        <button onClick={() => navigate("/home")}><ChevronLeft className="h-6 w-6" /></button>
        <h1 className="font-display text-lg font-bold">Settings</h1>
      </header>

      <div className="mx-auto max-w-2xl">
        {/* Profile picture */}
        <div className="flex flex-col items-center gap-3 border-b border-border px-5 py-7">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            data-testid="avatar-file-input"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onPickAvatar(f); e.target.value = ""; }}
          />
          <button
            data-testid="avatar-change"
            onClick={() => fileRef.current?.click()}
            disabled={avatarBusy}
            className="relative h-24 w-24"
          >
            <span className="flex h-full w-full items-center justify-center overflow-hidden rounded-full border border-border bg-input-bg font-display text-3xl font-bold">
              {avatarBusy ? (
                <Spinner className="h-6 w-6" />
              ) : account?.avatar_url ? (
                <img src={BACKEND_ORIGIN + account.avatar_url} alt="" className="h-full w-full object-cover" />
              ) : (
                initial
              )}
            </span>
            <span className="absolute bottom-0 right-0 flex h-7 w-7 items-center justify-center rounded-full border-2 border-background bg-primary text-primary-foreground">
              <Camera className="h-3.5 w-3.5" />
            </span>
          </button>
          <div className="flex items-center gap-3">
            <button data-testid="avatar-change-text" onClick={() => fileRef.current?.click()} disabled={avatarBusy} className="text-sm font-semibold text-primary">
              {account?.avatar_url ? "Change photo" : "Add a photo"}
            </button>
            {account?.avatar_url ? (
              <button data-testid="avatar-remove" onClick={onRemoveAvatar} disabled={avatarBusy} className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground">
                <Trash2 className="h-3.5 w-3.5" /> Remove
              </button>
            ) : null}
          </div>
        </div>

        {/* Theme */}
        <div className="flex flex-col gap-3 border-b border-border px-5 py-5">
          <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Theme color</span>
          <div className="grid grid-cols-4 gap-3">
            {THEME_LIST.map((t) => {
              const selected = (s.color_theme || "ocean") === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`theme-${t.id}`}
                  onClick={() => { applyTheme(t.id); save({ color_theme: t.id }); }}
                  className={cn(
                    "flex flex-col items-center gap-1.5 rounded-2xl border p-2 transition-all",
                    selected ? "border-primary ring-2 ring-primary" : "border-border hover:border-primary/40",
                  )}
                >
                  <span className="relative flex h-11 w-11 items-center justify-center rounded-full" style={{ background: t.bg }}>
                    <span className="h-5 w-5 rounded-full" style={{ background: t.accent }} />
                    {selected ? <span className="absolute inset-0 rounded-full ring-2 ring-inset" style={{ borderColor: t.accent }} /> : null}
                  </span>
                  <span className="text-[11px] font-medium">{t.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Name */}
        <div className="flex items-center justify-between gap-4 border-b border-border px-5 py-4">
          <span>Name</span>
          <input
            data-testid="setting-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => name.trim() && name !== account?.name && save({ name: name.trim() })}
            className="w-40 rounded-lg border border-input bg-background px-3 py-1.5 text-right text-sm outline-none focus:border-foreground"
          />
        </div>

        <Row label="Learning Level">
          <select data-testid="setting-level" className={selectCls} value={account?.learning_level || ""} onChange={(e) => save({ learning_level: e.target.value })}>
            {LEARNING_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </Row>

        <Row label="Voice">
          <select data-testid="setting-voice" className={selectCls} value={s.voice || "alloy"} onChange={(e) => save({ voice: e.target.value })}>
            {VOICES.map((v) => <option key={v} value={v}>{v[0].toUpperCase() + v.slice(1)}</option>)}
          </select>
        </Row>

        <div className="flex flex-col gap-3 border-b border-border px-5 py-4">
          <span>Text Size</span>
          <div className="flex gap-2">
            {TEXT_SIZES.map((t) => {
              const active = (s.text_size || "Medium") === t;
              return (
                <button key={t} data-testid={`text-size-${t}`} onClick={() => save({ text_size: t })}
                  className={cn("flex-1 rounded-lg border py-2 text-sm font-semibold", active ? "border-foreground bg-foreground text-background" : "border-border")}>
                  {t}
                </button>
              );
            })}
          </div>
        </div>

        <Row label="Language">
          <select data-testid="setting-language" className={selectCls} value={s.language || "English"} onChange={(e) => save({ language: e.target.value })}>
            {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </Row>

        <button data-testid="setting-help" onClick={() => window.open(HELP_URL, "_blank")} className="flex w-full items-center justify-between border-b border-border px-5 py-4 text-left">
          <span>Get Help</span>
          <ChevronLeft className="h-4 w-4 rotate-180 text-muted-foreground" />
        </button>

        {account?.parent_id ? (
          <div className="flex flex-col gap-1.5 px-5 py-5">
            <div className="flex items-center gap-2 text-muted-foreground"><Eye className="h-4 w-4" /><span className="text-sm font-semibold">A parent or guardian is linked</span></div>
            <p className="text-sm text-muted-foreground">They can view your activity and chat history from their own account. MAXx never sends them reports on its own. Only your parent or guardian can remove this link.</p>
          </div>
        ) : <ParentConnect />}

        {/* Log out */}
        <div className="px-5 py-6">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                data-testid="logout-button"
                className="flex w-full items-center justify-center gap-2 rounded-full border border-destructive/40 py-3 text-[15px] font-semibold text-destructive transition-opacity hover:opacity-80"
              >
                <LogOut className="h-4 w-4" /> Log out
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="logout-confirm-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Log out of MAXx?</AlertDialogTitle>
                <AlertDialogDescription>You'll need to sign in again to get back to your account.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="logout-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  data-testid="logout-confirm"
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={() => { signOut(); navigate("/"); }}
                >
                  Log out
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        <div className="h-10" />
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-border px-5 py-4">
      <span>{label}</span>
      {children}
    </div>
  );
}

function ParentConnect() {
  const { linkParentEmail } = useAuth();
  const [mode, setMode] = useState(null);
  const [email, setEmail] = useState("");
  const [confirm, setConfirm] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submitEmail = async () => {
    if (!email.includes("@")) return toast("Enter a valid email.");
    if (email.trim().toLowerCase() !== confirm.trim().toLowerCase()) return toast("The emails don't match.");
    if (!consent) return toast("Please confirm parent/guardian consent.");
    setBusy(true);
    try { await linkParentEmail({ parent_email: email.trim(), parent_email_confirm: confirm.trim(), consent: true }); toast("Parent or guardian linked."); }
    catch (e) { toast(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col gap-3 px-5 py-5">
      <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Parent / Guardian</span>
      <p className="text-sm text-muted-foreground">Optionally connect a parent or guardian so they can help manage your account. Once linked, only they can remove it.</p>
      {mode === null ? (
        <div className="flex flex-col gap-2">
          <Button variant="secondary" data-testid="connect-parent-email" onClick={() => setMode("email")}>Link with a parent's email</Button>
          <p className="text-xs text-muted-foreground">Already have your own account on a parent's device? In the MAXx mobile app, tap <b>Scan</b> and point it at your parent's Link QR to connect.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <Field label="Parent / guardian email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@email.com" data-testid="link-parent-email" />
          <Field label="Confirm email" type="email" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="name@email.com" data-testid="link-parent-email-confirm" />
          <button data-testid="link-consent" onClick={() => setConsent((c) => !c)} className="flex items-start gap-3 text-left">
            <span className={cn("mt-0.5 flex h-6 w-6 items-center justify-center rounded-md border-2 border-foreground", consent && "bg-primary")}>
              {consent ? <Check className="h-4 w-4 text-primary-foreground" /> : null}
            </span>
            <span className="flex-1 text-sm">A parent or guardian consents to linking this account.</span>
          </button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setMode(null)}>Cancel</Button>
            <Button className="flex-1" loading={busy} data-testid="submit-link-email" onClick={submitEmail}>Link</Button>
          </div>
        </div>
      )}
    </div>
  );
}
